import { IDBPDatabase, openDB } from 'idb'
import type {
  CSVExportDB,
  ExportConfig,
  ExportError,
  ExportEvent,
  ExportHistoryRecord,
  ExportProgress,
  ExportRecord,
  ExportStatus,
  ExportSubscriber,
  StorageUsage,
} from './types'

const DB_NAME = 'scalis-csv-exports'
const DB_VERSION = 1
const EXPORT_STORE = 'exports'
const HISTORY_STORE = 'export-history'

export class CSVExportManager {
  private db: IDBPDatabase<CSVExportDB> | null = null
  private subscribers: Set<ExportSubscriber> = new Set()
  private activeWorkers: Map<string, Worker> = new Map()
  private cancellingExports: Set<string> = new Set()

  async initializeDB(): Promise<void> {
    try {
      this.db = await openDB<CSVExportDB>(DB_NAME, DB_VERSION, {
        upgrade(db) {
          if (!db.objectStoreNames.contains(EXPORT_STORE)) {
            const exportStore = db.createObjectStore(EXPORT_STORE, { keyPath: 'id' })
            exportStore.createIndex('by-status', 'status.state')
            exportStore.createIndex('by-created', 'createdAt')
            exportStore.createIndex('by-user', 'metadata.userId')
          }

          if (!db.objectStoreNames.contains(HISTORY_STORE)) {
            const historyStore = db.createObjectStore(HISTORY_STORE, { keyPath: 'id' })
            historyStore.createIndex('by-created', 'createdAt')
            historyStore.createIndex('by-user', 'userId')
            historyStore.createIndex('by-expires', 'expiresAt')
          }
        },
      })
    } catch {
      throw new Error('Failed to initialize export database')
    }
  }

  async createExport(config: ExportConfig, userId: string): Promise<string> {
    if (!this.db) throw new Error('Database not initialized')

    const exportId = this.generateId()
    const now = new Date()

    const { queryHook: _queryHook, ...storedConfig } = config

    const exportRecord: ExportRecord = {
      id: exportId,
      status: {
        state: 'queued',
        currentPage: 0,
        totalPages: 0,
        currentRecords: 0,
        totalRecords: 0,
        percentage: 0,
        message: 'Export queued for processing',
      },
      config: storedConfig,
      progress: {
        fetchedPages: 0,
        totalRecords: 0,
        estimatedTimeRemaining: 0,
        startTime: now,
        lastPageTime: now,
        averagePageTime: 0,
        retryCount: 0,
      },
      createdAt: now,
      updatedAt: now,
      downloadFilename: this.generateFilename(config.filename),
      metadata: {
        userId,
        tableName: config.filename.replace('.csv', ''),
        tableType: this.extractTableType(config.dataPath),
        recordCount: 0,
      },
    }

    try {
      await this.db.add(EXPORT_STORE, exportRecord)
      this.notifySubscribers({
        type: 'created',
        exportId,
        timestamp: now,
      })

      return exportId
    } catch {
      throw new Error('Failed to create export')
    }
  }

  async updateExportProgress(
    exportId: string,
    progressUpdate: Partial<ExportProgress>,
    statusUpdate?: Partial<ExportRecord['status']>,
  ): Promise<void> {
    if (!this.db) throw new Error('Database not initialized')

    try {
      const exportRecord = await this.db.get(EXPORT_STORE, exportId)
      if (!exportRecord) {
        throw new Error(`Export ${exportId} not found`)
      }

      exportRecord.progress = { ...exportRecord.progress, ...progressUpdate }

      if (statusUpdate) {
        exportRecord.status = { ...exportRecord.status, ...statusUpdate }
      }

      if (exportRecord.status.totalPages > 0) {
        exportRecord.status.percentage = Math.round(
          (exportRecord.status.currentPage / exportRecord.status.totalPages) * 100,
        )
      }

      exportRecord.updatedAt = new Date()
      if (progressUpdate.lastPageTime) {
        exportRecord.progress.lastPageTime = progressUpdate.lastPageTime
      }

      if (exportRecord.progress.fetchedPages > 0) {
        const totalTime = exportRecord.updatedAt.getTime() - exportRecord.progress.startTime.getTime()
        exportRecord.progress.averagePageTime = totalTime / exportRecord.progress.fetchedPages
      }

      if (
        exportRecord.status.totalPages > exportRecord.status.currentPage &&
        exportRecord.progress.averagePageTime > 0
      ) {
        const remainingPages = exportRecord.status.totalPages - exportRecord.status.currentPage
        exportRecord.progress.estimatedTimeRemaining = remainingPages * exportRecord.progress.averagePageTime
      }

      await this.db.put(EXPORT_STORE, exportRecord)

      this.notifySubscribers({
        type: 'progress',
        exportId,
        timestamp: new Date(),
        data: { progress: exportRecord.progress, status: exportRecord.status },
      })
    } catch {
      throw new Error('Failed to update export progress')
    }
  }

  async completeExport(exportId: string, downloadUrl: string, fileSize: number): Promise<void> {
    if (!this.db) throw new Error('Database not initialized')

    try {
      const exportRecord = await this.db.get(EXPORT_STORE, exportId)
      if (!exportRecord) {
        throw new Error(`Export ${exportId} not found`)
      }

      const now = new Date()
      exportRecord.status.state = 'completed'
      exportRecord.status.percentage = 100
      exportRecord.status.message = 'Export completed successfully'
      exportRecord.downloadUrl = downloadUrl
      exportRecord.completedAt = now
      exportRecord.updatedAt = now

      await this.db.put(EXPORT_STORE, exportRecord)

      await this.addToHistory(exportRecord, fileSize)
      this.notifySubscribers({
        type: 'completed',
        exportId,
        timestamp: now,
        data: { downloadUrl },
      })
    } catch {
      throw new Error('Failed to complete export')
    }
  }

  async markExportError(exportId: string, error: ExportError): Promise<void> {
    if (!this.db) throw new Error('Database not initialized')

    try {
      const exportRecord = await this.db.get(EXPORT_STORE, exportId)
      if (!exportRecord) {
        throw new Error(`Export ${exportId} not found`)
      }

      exportRecord.status.state = 'error'
      exportRecord.status.message = error.message
      exportRecord.error = error
      exportRecord.updatedAt = new Date()

      await this.db.put(EXPORT_STORE, exportRecord)

      this.notifySubscribers({
        type: 'error',
        exportId,
        timestamp: new Date(),
        data: { error },
      })
    } catch {
      throw new Error('Failed to update export error state')
    }
  }

  async cancelExport(exportId: string): Promise<void> {
    if (!this.db) throw new Error('Database not initialized')

    if (this.cancellingExports.has(exportId)) {
      return
    }

    try {
      this.cancellingExports.add(exportId)

      const exportRecord = await this.db.get(EXPORT_STORE, exportId)
      if (!exportRecord) {
        throw new Error(`Export ${exportId} not found`)
      }

      if (exportRecord.status.state === 'completed') {
        const thirtySecondsAgo = Date.now() - 30 * 1000
        if (exportRecord.updatedAt.getTime() > thirtySecondsAgo) {
          return
        }
      }

      const worker = this.activeWorkers.get(exportId)
      if (worker) {
        worker.terminate()
        this.activeWorkers.delete(exportId)
      }

      exportRecord.status.state = 'cancelled'
      exportRecord.status.message = 'Export cancelled by user'
      exportRecord.updatedAt = new Date()

      await this.db.put(EXPORT_STORE, exportRecord)

      this.notifySubscribers({
        type: 'cancelled',
        exportId,
        timestamp: new Date(),
      })
    } catch {
      throw new Error('Failed to cancel export')
    } finally {
      this.cancellingExports.delete(exportId)
    }
  }

  async getActiveExports(userId?: string): Promise<ExportRecord[]> {
    if (!this.db) throw new Error('Database not initialized')

    try {
      let exports: ExportRecord[]

      if (userId) {
        exports = await this.db.getAllFromIndex(EXPORT_STORE, 'by-user', userId)
      } else {
        exports = await this.db.getAll(EXPORT_STORE)
      }

      const filteredExports = exports.filter((exp) => {
        if (exp.isDismissed) {
          return false
        }

        const isActive = !['completed', 'cancelled', 'error'].includes(exp.status.state)
        const isCompleted = exp.status.state === 'completed'
        const isError = exp.status.state === 'error'

        return isActive || isCompleted || isError
      })

      return filteredExports
    } catch {
      return []
    }
  }

  async getExportById(exportId: string): Promise<ExportRecord | null> {
    if (!this.db) throw new Error('Database not initialized')

    try {
      return (await this.db.get(EXPORT_STORE, exportId)) || null
    } catch {
      return null
    }
  }

  private async addToHistory(exportRecord: ExportRecord, fileSize: number): Promise<void> {
    const historyRecord: ExportHistoryRecord = {
      id: exportRecord.id,
      filename: exportRecord.downloadFilename,
      downloadUrl: exportRecord.downloadUrl,
      fileSize,
      recordCount: exportRecord.progress.totalRecords,
      tableName: exportRecord.metadata.tableName,
      filters: JSON.stringify(exportRecord.config.variables),
      downloadCount: 0,
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      createdAt: exportRecord.createdAt,
      userId: exportRecord.metadata.userId,
    }

    await this.db!.add(HISTORY_STORE, historyRecord)
  }

  async getExportHistory(userId?: string, limit: number = 50): Promise<ExportHistoryRecord[]> {
    if (!this.db) throw new Error('Database not initialized')

    try {
      let history: ExportHistoryRecord[]

      if (userId) {
        history = await this.db.getAllFromIndex(HISTORY_STORE, 'by-user', userId)
      } else {
        history = await this.db.getAll(HISTORY_STORE)
      }

      return history.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()).slice(0, limit)
    } catch {
      return []
    }
  }

  async markAsDownloaded(historyId: string): Promise<void> {
    if (!this.db) throw new Error('Database not initialized')

    try {
      const historyRecord = await this.db.get(HISTORY_STORE, historyId)
      if (historyRecord) {
        historyRecord.downloadedAt = new Date()
        historyRecord.downloadCount += 1
        await this.db.put(HISTORY_STORE, historyRecord)
      }
    } catch {
      // Silently handle errors
    }
  }

  async cleanupExpiredExports(): Promise<void> {
    if (!this.db) throw new Error('Database not initialized')

    try {
      const now = new Date()

      const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000)
      const allExports = await this.db.getAll(EXPORT_STORE)

      for (const exportRecord of allExports) {
        if (
          exportRecord.createdAt < oneDayAgo &&
          ['completed', 'cancelled', 'error'].includes(exportRecord.status.state)
        ) {
          await this.db.delete(EXPORT_STORE, exportRecord.id)

          if (exportRecord.downloadUrl) {
            URL.revokeObjectURL(exportRecord.downloadUrl)
          }
        }
      }

      const expiredHistory = await this.db.getAllFromIndex(HISTORY_STORE, 'by-expires', IDBKeyRange.upperBound(now))

      for (const historyRecord of expiredHistory) {
        await this.db.delete(HISTORY_STORE, historyRecord.id)

        if (historyRecord.downloadUrl) {
          URL.revokeObjectURL(historyRecord.downloadUrl)
        }
      }
    } catch {
      // Silently handle errors
    }
  }

  async getStorageUsage(): Promise<StorageUsage> {
    try {
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        const estimate = await navigator.storage.estimate()
        const used = estimate.usage || 0
        const available = estimate.quota || 0
        const percentage = available > 0 ? (used / available) * 100 : 0

        return { used, available, percentage }
      } else {
        return { used: 0, available: 0, percentage: 0 }
      }
    } catch {
      return { used: 0, available: 0, percentage: 0 }
    }
  }

  async optimizeStorage(): Promise<void> {
    await this.cleanupExpiredExports()

    const usage = await this.getStorageUsage()
    if (usage.percentage > 80) {
      await this.forceCleanupOldest(10)
    }
  }

  private async forceCleanupOldest(count: number): Promise<void> {
    if (!this.db) return

    try {
      const allHistory = await this.db.getAll(HISTORY_STORE)
      const sortedHistory = allHistory
        .sort((a: ExportHistoryRecord, b: ExportHistoryRecord) => a.createdAt.getTime() - b.createdAt.getTime())
        .slice(0, count)

      for (const record of sortedHistory) {
        await this.db.delete(HISTORY_STORE, record.id)
        if (record.downloadUrl) {
          URL.revokeObjectURL(record.downloadUrl)
        }
      }
    } catch {
      // Silently handle errors
    }
  }

  async dismissExport(exportId: string): Promise<void> {
    if (!this.db) {
      throw new Error('Database not initialized')
    }

    const existingExport = await this.db.get(EXPORT_STORE, exportId)
    if (!existingExport) {
      throw new Error(`Export ${exportId} not found`)
    }

    const updatedExport: ExportRecord = {
      ...existingExport,
      isDismissed: true,
      updatedAt: new Date(),
    }

    await this.db.put(EXPORT_STORE, updatedExport)

    this.notifySubscribers({
      type: 'export-dismissed',
      exportId,
      timestamp: new Date(),
    })
  }

  subscribe(callback: ExportSubscriber): () => void {
    this.subscribers.add(callback)
    return () => {
      this.subscribers.delete(callback)
    }
  }

  private notifySubscribers(event: ExportEvent): void {
    this.subscribers.forEach((callback) => {
      try {
        callback(event)
      } catch {
        // Silently handle errors
      }
    })
  }

  private generateId(): string {
    return `export_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  private generateFilename(baseFilename: string): string {
    const timestamp = new Date().toISOString().split('T')[0]
    const name = baseFilename.replace('.csv', '')
    return `${name}_${timestamp}.csv`
  }

  private extractTableType(dataPath: string): string {
    const parts = dataPath.split('.')
    return parts[0] || 'unknown'
  }

  async resumeActiveExports(): Promise<void> {
    try {
      const activeExports = await this.getActiveExports()
      const resumableStates: ExportStatus[] = ['queued', 'initializing', 'fetching']

      for (const exportRecord of activeExports) {
        if (resumableStates.includes(exportRecord.status.state)) {
          await this.updateExportProgress(
            exportRecord.id,
            {},
            {
              state: 'initializing',
              message: 'Resuming export...',
            },
          )
        }
      }
    } catch {
      // Silently handle errors
    }
  }
}

let exportManagerInstance: CSVExportManager | null = null

export const getCSVExportManager = (): CSVExportManager => {
  if (!exportManagerInstance) {
    exportManagerInstance = new CSVExportManager()
  }
  return exportManagerInstance
}
